(ns dre.pages.dictionary.term
  (:require
   ["antd" :refer [Row Col Divider]]
   [clojure.set :as set]
   [clojure.string :as str]
   [dre.app.router :as router :refer [->route ->route-with-query-params]]
   [dre.components.icon :refer [fa-icon]]
   [dre.components.list :refer [collapsable-list]]
   [dre.components.pill :as pill]
   [dre.components.subject :as subject]
   [dre.components.table :refer [table]]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.coll :refer [provide-coll distinct-by]]
   [dre.i18n.events :as-alias i18n]
   [dre.i18n.views :refer [tr]]
   [dre.pages.dictionary.util :as tutil]
   [dre.pages.dictionary.events :as events]
   [dre.page-util :refer [md]]
   [re-frame.core :as rf]))

;; utils

(defn secure-url? [term]
  (when term
    (boolean (re-matches #"^https://.*" term))))

(def bioportal-curie-registry
  {"HPO" "HP"
   "UATS" "ATC"})

(defn valid-curie?
  [registry-part local-id-part]
  (and (not (str/blank? registry-part))
       (not (str/blank? local-id-part))
       (not (contains? #{"MEDLINEPLUS" "UMLS"} registry-part))
       (not (str/starts-with? local-id-part "MTHU"))))

(defn term-curie
  [uri]
  (if-not (string? uri)
    uri
    (let [bioportal-prefix "http://purl.bioontology.org/ontology/"
          umls-prefix "http://vocab.drevidence.com/UMLS#"
          ul (count uri)
          bioportal-l (count bioportal-prefix)
          umls-l (count "http://vocab.drevidence.com/")
          id-part (cond (and (str/starts-with? uri bioportal-prefix) (< bioportal-l ul))
                        (subs uri bioportal-l)
                        (and (str/starts-with? uri umls-prefix) (< umls-l ul))
                        (subs uri umls-l)
                        :else nil)
          id-part (when-not (str/blank? id-part)
                    (js/decodeURIComponent id-part))
          some-id? (not (str/blank? id-part))
          separator-idx (when some-id?
                          (or (str/index-of id-part "/")
                              (str/index-of id-part ":")
                              (str/index-of id-part "#")))
          separator-idx (when (and separator-idx
                                   (< separator-idx (dec (count id-part))))
                          separator-idx)
          registry-part (if (and some-id? separator-idx)
                          (subs id-part 0 separator-idx)
                          id-part)
          registry-part (get bioportal-curie-registry registry-part registry-part)
          local-id-part (when (and some-id? separator-idx)
                          (subs id-part (inc separator-idx)))
          local-id-part (if (and (not (str/blank? registry-part))
                                 (not (str/blank? local-id-part))
                                 (< (inc (count registry-part)) (count local-id-part))
                                 (str/starts-with? local-id-part registry-part))
                          (subs local-id-part (inc (count registry-part)))
                          local-id-part)]
      {:curie/uri uri
       :curie/prefix registry-part
       :curie/identifier local-id-part
       :curie/curie (cond (and registry-part local-id-part)
                          (str registry-part ":" local-id-part)
                          registry-part registry-part
                          :else nil)
       :curie/valid? (valid-curie? registry-part local-id-part)})))

(defn source-vocabs
  [terms]
  (set/difference
   (into #{} (map tutil/extract-term-from-url
                  (distinct
                   (mapcat #(provide-coll (get % :source-vocab)) terms))))
   #{"DRUGBANK" "MEDDRA"}))

(defn alt-labels
  [terms title]
  (sort
   (remove #(= ((fnil str/lower-case "") title)
               ((fnil str/lower-case "") %))
           (distinct-by
            (fnil str/lower-case "")
            (concat (mapcat tutil/alt-labels terms)
                    (map #(get % :term/label) terms))))))

(def adhoc-alt-phrases-entries
  ;; copied from workspace/components/index/src/dre/index/alt_phrases.clj
  ;; #3252
  {"SURXGT" [["SURMex" "SUDeIQ" "SUOKun"]] ;; biktarvy
   "SUMLHf" [["SUDeIQ" "SUF-hr" "SUBdzj"]] ;; complera
   "SUQHda" [["SUDeIQ" "SUOKun"]] ;; descovy
   "SUPe7j" [["SUGfIl" "SUMHsU" "SUDeIQ" "SUOKun"]] ;; genvoya
   "SUPlEE" [["SUDeIQ" "SUF-hr" "SUOKun"]] ;; odefsey
   "SUNTNN" [["SUGfIl" "SUMHsU" "SUDeIQ" "SUBdzj"]] ;; stribild
   "SUF1Kv" [["SUDeIQ" "SUBdzj"]] ;; truvada
   "SUGk5x" [["SUBeiR" "SUDeIQ" "SUBdzj"]] ;; atripla
   })

(def adhoc-alt-phrases-label-map
  {"SUF-hr" "Rilpivirine",
   "SURMex" "Bictegravir",
   "SUBeiR" "Efavirenz",
   "SUMHsU" "Cobicistat",
   "SUBdzj" "Tenofovir",
   "SUGfIl" "Elvitegravir",
   "SUOKun" "Tenofovir alafenamide",
   "SUDeIQ" "Emtricitabine"})

(defn combinations-pills
  [phrase]
  (vec
   (interpose {:pill/type :syntax
               :pill/query "with"
               :pill/label "WITH"}
              (map #(hash-map :pill/query %
                              :pill/label (adhoc-alt-phrases-label-map %)
                              :pill/type :term) phrase))))

;; views

(defn link
  [label-map guid-map uri]
  (let [guid (get guid-map (keyword uri))
        label (get label-map (keyword uri))
        {:keys [:curie/curie :curie/valid?]} (term-curie uri)
        route-params (events/route-params (guid-map (keyword uri)))]
    (cond
      guid
      [:<>
       [:a {:href (->route-with-query-params (->route :dictionary {:guid (:guid route-params)}) (:query-params route-params))}
        (or label curie)]
       (when (and curie valid?)
         [:<>
          " ("
          [:a {:href (str "https://bioregistry.io/" curie)}
           curie]
          ")"])]
      (and curie valid?)
      [:a {:href (str "https://bioregistry.io/" curie)}
       curie]
      :else
      (if (secure-url? uri)
        [:a {:href uri}
         uri]
        [:span uri]))))

(defn term-content-header
  [{:keys [sense title]}]
  [:<>
   [:> Row
    {:gutter 16}
    [:> Col
     {:span 4}
     [:b [tr "Representation"]]]
    [:> Col
     {:span 20}
     [subject/phrase [{:sense sense :label title}] nil]]]
   [:> Divider]])

(defn vocabs-row
  [{:keys [terms]}]
  (let [vocabularies (set/union
                      (source-vocabs terms)
                      (set/difference
                       (into #{} (mapv tutil/term-vocabulary terms))
                       #{"UMLS"}))]
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Vocabularies"]]]
      [:> Col
       {:span 20}
       (str/join ", " vocabularies)]]
     [:> Divider]]))

(defn normalized-label
  [label]
  (when-not (str/blank? label)
    (let [label (str/trim (str/lower-case (str/replace label #"\W" " ")))
          tokens (remove str/blank? (str/split label #"\W"))]
      (str/join " " (sort tokens)))))

(defn alt-labels-row
  [{:keys [terms _title]}]
  (let [lf (fn [term]
             (let [vocab (tutil/term-vocabulary term)
                   alts (:term/norm-alts term)]
               (cons
                [(:term/norm-label term) (:term/label term) vocab true]
                (map #(vector (first %) (second %) vocab false) alts))))
        labels (mapcat lf terms)
        coll (group-by first labels)
        mf (fn [[nlabel labels]]
             (when-not (str/blank? nlabel)
               {:vocabs (vec (sort (distinct (map #(nth % 2) labels))))
                :label (first (sort (distinct (map second labels))))
                :labels (sort (distinct (map second labels)))
                :primary-vocabs (set (map #(nth % 2)
                                          (filter (comp true? #(nth % 3))
                                                  labels)))}))
        rows (sort-by :label (keep mf coll))]
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Alternative labels (Synonyms)"]]]
      [:> Col
       {:span 20}
       [:div
        {:style {:width "100%"}}
        [table
         {:columns [{:title "Synonym"
                     :render (fn [data]
                               (tooltip-wrapper
                                (when-let [coll (next (get data :labels))]
                                  (str "Alternative spelling variants: "
                                       (str/join "; " coll)))
                                (if (get data :primary)
                                  [:i (get data :label)]
                                  (get data :label))))}
                    {:title "Dictionaries"
                     :render (fn [data]
                               (let [vocabs (provide-coll (get data :vocabs))
                                     pset (set (get data :primary-vocabs))
                                     xf (fn [vocab]
                                          (if (contains? pset vocab)
                                            [tooltip-wrapper
                                             (str "Preferred term for " vocab)
                                             [:i vocab]]
                                            [:span vocab]))]
                                 [:<>
                                  (interpose [:span ", "] (map xf vocabs))]))}]
          :size :small
          :dataSource rows
          :pagination {:defaultPageSize 30
                       :hideOnSinglePage true}}]]]]
     [:> Divider]]))

(defn drug-combinations-row
  [{:keys [sense]}]
  (if-let [phrases (adhoc-alt-phrases-entries sense)]
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Drug combinations"]]]
      [:> Col
       {:span 20}
       [:<>
        [:p [tr "Will match if all combinations match in the same sentence."]]
        (doall
         (for [phrase phrases]
           ^{:key phrase}
           (let [pills (combinations-pills phrase)]
             [pill/readonly-pills pills nil nil nil {}])))]]]
     [:> Divider]]
    nil))

(defn also-included-row
  [completed-pill]
  (when-let [included (:pill/includes-pills completed-pill)]
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Also included"]]]
      [:> Col
       {:span 20}
       [:<>
        [:p [tr "Shows the first 1000 expansions. List is sorted by overall popularity."]]
        (doall
         (for [[idx pill] (map-indexed vector included)]
           ^{:key idx}
           [subject/phrase [{:sense (:pill/query pill) :label (:pill/label pill)}] nil]))]]]
     [:> Divider]]))

(defn acronyms-row
  [acronyms]
  (let [{:keys [lex/known-acronyms lex/potential-ambiguity]} acronyms]
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Acronyms"]]]
      [:> Col
       {:span 20}
       (if-not (seq known-acronyms)
         [:p [tr "No acronyms or abbreviations found"]]
         [:div
          (doall
           (for [acro known-acronyms]
             (let [ambi (get potential-ambiguity (keyword acro))]
               (with-meta
                 (if (seq ambi)
                   [:div
                    {:style {:padding-bottom "1rem"}}
                    [:div {:style {:font-weight "bold" :display "flex"}}
                     (when (> (count ambi) 1)
                       [tooltip-wrapper (str "This acronym has multiple definitions and therefore is not included in the current search unless listed in the synonyms list above. "
                                             "To include the acronym in your search, add a keyword search of the acronym as an optional term. See FAQs + Tips under “Need Help?” for more details.")
                        [:span
                         {:style {:width "20px"}}
                         [fa-icon :warning]]])
                     acro]
                    [collapsable-list
                     (mapv (fn [item]
                             {:content (first (provide-coll item))}) (get potential-ambiguity (keyword acro)))
                     3]]
                   [:span {:style {:font-weight "bold"}} acro])
                 {:key acro}))))])]]
     [:> Divider]]))

(defn definitions-row
  [{:keys [terms]}]
  (let [definitions (keep tutil/term-definition terms)]
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Definitions"]]]
      [:> Col
       {:span 20}
       (if (seq definitions)
         [:div
          (doall
           (for [[vocab definition] (distinct definitions)]
             ^{:key (hash definition)}
             [:div [:span {:style {:font-weight "bold"}} vocab]
              (md definition)]))]
         [:p [tr "No definitions found"]])]]
     [:> Divider]]))

(defn term-license-note
  []
  [:p
   {:style {:padding-top "1rem"}}
   [:strong [tr "License note"]]
   [:br]
   [:em [tr "This ontology contains material from the UMLS
   Metathesaurus. Some material in the UMLS Metathesaurus is from
   copyrighted sources of the respective copyright holders. Users of
   the UMLS Metathesaurus are solely responsible for compliance with
   any copyright, patent or trademark restrictions and are referred to
   the copyright, patent or trademark notices appearing in the
   original sources, all of which are hereby incorporated by
   reference. "] [:a {:href "https://uts.nlm.nih.gov/license.html"}
                  "https://uts.nlm.nih.gov/license.html"]]])
